import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Application {
  id: string
  name: string
  status: string
  response_time_avg_ms: number
  error_rate: number
  throughput_rpm: number
  apdex_score: number
}

interface Transaction {
  id: string
  application_name: string
  name: string
  status: string
  avg_duration_ms: number
  throughput_rpm: number
}

interface APMStats {
  total_applications: number
  healthy_applications: number
  avg_response_time_ms: number
  total_transactions_24h: number
  error_rate_avg: number
  avg_apdex_score: number
}

const APMPage = () => {
  const [applications, setApplications] = useState<Application[]>([])
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [stats, setStats] = useState<APMStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('applications')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [appsRes, transRes, statsRes] = await Promise.all([
        fetch('/api/apm/applications'),
        fetch('/api/apm/transactions'),
        fetch('/api/apm/statistics')
      ])

      setApplications(await appsRes.json())
      setTransactions(await transRes.json())
      setStats(await statsRes.json())
    } catch (error) {
      console.error('Error fetching APM data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800'
      case 'degraded': return 'bg-yellow-100 text-yellow-800'
      case 'critical': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getApdexColor = (score: number) => {
    if (score >= 0.94) return 'text-green-600'
    if (score >= 0.85) return 'text-blue-600'
    if (score >= 0.70) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Application Performance Monitoring</h1>
          <p className="text-gray-600">Monitor application health, performance, and errors</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Applications</p>
              <p className="text-2xl font-bold">{stats.total_applications}</p>
              <p className="text-xs text-green-600">{stats.healthy_applications} healthy</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Avg Response Time</p>
              <p className="text-2xl font-bold">{stats.avg_response_time_ms}ms</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Transactions (24h)</p>
              <p className="text-2xl font-bold">{stats.total_transactions_24h.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Error Rate</p>
              <p className="text-2xl font-bold">{stats.error_rate_avg}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Avg Apdex</p>
              <p className={`text-2xl font-bold ${getApdexColor(stats.avg_apdex_score)}`}>
                {stats.avg_apdex_score}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Status</p>
              <p className="text-2xl font-bold text-green-600">Operational</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['applications', 'transactions'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-6 text-sm font-medium capitalize ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4">
            {activeTab === 'applications' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Throughput</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Apdex</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{app.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(app.status)}`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{app.response_time_avg_ms}ms</td>
                        <td className="px-6 py-4 whitespace-nowrap">{app.error_rate}%</td>
                        <td className="px-6 py-4 whitespace-nowrap">{app.throughput_rpm} rpm</td>
                        <td className={`px-6 py-4 whitespace-nowrap font-medium ${getApdexColor(app.apdex_score)}`}>
                          {app.apdex_score}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Throughput</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.slice(0, 20).map((trans) => (
                      <tr key={trans.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{trans.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{trans.application_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(trans.status)}`}>
                            {trans.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{trans.avg_duration_ms}ms</td>
                        <td className="px-6 py-4 whitespace-nowrap">{trans.throughput_rpm} rpm</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default APMPage
